<template>
  <div class="dashboard-home-stats">
    <div class="dashboard-section-title">
      <div>
        <h3>آمار فروش</h3>
        <p class="text-muted mb-0">کل فروش، امروز، این ماه و امسال</p>
      </div>
      <m-a class="home-stats-link" :href="{name:'Statistic'}">آمار کامل</m-a>
    </div>

    <div class="row no-gutters">
        <div
          v-for="card in periodCards"
          :key="card.key"
          class="col-xl-3 col-md-6 mb-3 px-2"
        >
          <div class="home-kpi" :class="'is-' + card.key">
            <div v-if="loading" class="home-kpi-loading">
              <div class="spinner-border spinner-border-sm text-light" role="status"></div>
            </div>
            <template v-else>
              <div class="home-kpi-icon">
                <i :class="card.icon"></i>
              </div>
              <div class="home-kpi-copy">
                <span>{{ card.label }}</span>
                <strong>{{ formatMoney(card.amount) }}</strong>
                <small>{{ formatCount(card.count) }} سفارش</small>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="dashboard-section-title">
        <div>
          <h3>شاخص‌های کلیدی</h3>
          <p class="text-muted mb-0">رشد، بهترین روز و بزرگ‌ترین سفارش</p>
        </div>
      </div>

      <div class="row no-gutters">
        <div class="col-lg-4 mb-3 px-2">
          <div class="home-insight h-100" :class="loading ? '' : (growthValue >= 0 ? 'is-growth' : 'is-growth-down')">
            <div v-if="loading" class="home-kpi-loading">
              <div class="spinner-border spinner-border-sm text-light" role="status"></div>
            </div>
            <template v-else>
              <i :class="growthValue >= 0 ? 'bi bi-graph-up-arrow' : 'bi bi-graph-down-arrow'"></i>
              <div>
                <span>رشد نسبت به ماه قبل</span>
                <strong>{{ formattedGrowth }}</strong>
              </div>
            </template>
          </div>
        </div>
        <div class="col-lg-4 mb-3 px-2">
          <div class="home-insight is-best h-100">
            <div v-if="loading" class="home-kpi-loading">
              <div class="spinner-border spinner-border-sm text-light" role="status"></div>
            </div>
            <template v-else>
              <i class="bi bi-calendar-heart"></i>
              <div>
                <span>بهترین روز فروش</span>
                <strong>{{ bestDayLabel }}</strong>
                <small>{{ formatMoney(bestDay.sales_amount) }}</small>
              </div>
            </template>
          </div>
        </div>
        <div class="col-lg-4 mb-3 px-2">
          <div class="home-insight is-big h-100">
            <div v-if="loading" class="home-kpi-loading">
              <div class="spinner-border spinner-border-sm text-light" role="status"></div>
            </div>
            <template v-else>
              <i class="bi bi-trophy-fill"></i>
              <div>
                <span>بزرگ‌ترین سفارش</span>
                <strong>{{ formatMoney(highlights.biggest_order_amount) }}</strong>
                <small>{{ biggestCustomer }}</small>
              </div>
            </template>
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import {formatNumber} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter"

const JALALI_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
]

export default {
  name: "DashboardHomeStats",
  data() {
    return {
      loading: true,
      biggestOrder: {},
      sales: {
        total: {orders_count: 0, sales_amount: 0},
        today: {orders_count: 0, sales_amount: 0},
        this_month: {orders_count: 0, sales_amount: 0},
        this_year: {orders_count: 0, sales_amount: 0},
        highlights: {
          growth_vs_last_month_percent: 0,
          biggest_order_amount: 0,
          best_day: {},
        },
      },
    }
  },
  computed: {
    highlights() {
      return this.sales.highlights || {}
    },
    bestDay() {
      return this.highlights.best_day || {}
    },
    growthValue() {
      const value = Number(this.highlights.growth_vs_last_month_percent)
      return isNaN(value) ? 0 : value
    },
    formattedGrowth() {
      const sign = this.growthValue > 0 ? '+' : ''
      return sign + this.growthValue + '٪'
    },
    bestDayLabel() {
      if (!this.bestDay.jalali_date) {
        return 'بدون داده'
      }
      const monthName = JALALI_MONTHS[(this.bestDay.month || 1) - 1] || ''
      return (this.bestDay.day || '') + ' ' + monthName
    },
    biggestCustomer() {
      const user = this.biggestOrder.user || {}
      const name = ((user.name || '') + ' ' + (user.last_name || '')).trim()
      return name || 'مشتری فروشگاه'
    },
    periodCards() {
      return [
        {key: 'total', label: 'کل فروش', icon: 'bi bi-wallet2', amount: this.sales.total.sales_amount, count: this.sales.total.orders_count},
        {key: 'today', label: 'امروز', icon: 'bi bi-calendar-day', amount: this.sales.today.sales_amount, count: this.sales.today.orders_count},
        {key: 'month', label: 'این ماه', icon: 'bi bi-calendar3', amount: this.sales.this_month.sales_amount, count: this.sales.this_month.orders_count},
        {key: 'year', label: 'امسال', icon: 'bi bi-calendar-check', amount: this.sales.this_year.sales_amount, count: this.sales.this_year.orders_count},
      ]
    },
  },
  methods: {
    formatCount(value) {
      return formatNumber(value || 0)
    },
    formatMoney(value) {
      return formatNumber(value || 0) + ' تومان'
    },
    async loadStats() {
      this.loading = true
      try {
        const [salesRes, biggestRes] = await Promise.all([
          this.$store.dispatch('Statistic/sales'),
          this.$store.dispatch('Statistic/biggestSell'),
        ])
        this.sales = Object.assign({}, this.sales, (salesRes.data && salesRes.data.data) || {})
        this.biggestOrder = (biggestRes.data && biggestRes.data.data) || {}
      } catch (e) {
        this.biggestOrder = {}
      } finally {
        this.loading = false
      }
    },
  },
  mounted() {
    this.loadStats()
  },
}
</script>

<style scoped>
.dashboard-home-stats {
  overflow-x: hidden;
  margin-bottom: 8px;
}

.home-kpi-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-stats-loading {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-stats-link {
  background: #eb3d60;
  color: #fff !important;
  border-radius: 12px;
  padding: 8px 14px;
  font-weight: 800;
  text-decoration: none !important;
}

.home-kpi {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  min-height: 96px;
  padding: 16px 14px;
  border-radius: 18px;
  color: #fff;
  background: #1e1e1e;
  box-shadow: 0 10px 22px rgba(30, 30, 30, 0.12);
}

.home-kpi::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(rgba(255, 255, 255, 0.07) 1px, transparent 1px);
  background-size: 12px 12px;
  pointer-events: none;
}

.home-kpi > * {
  position: relative;
  z-index: 1;
}

.home-kpi span,
.home-kpi small {
  display: block;
  opacity: 0.78;
}

.home-kpi-copy {
  min-width: 0;
  width: 100%;
  text-align: center;
}

.home-kpi-copy strong {
  display: block;
  font-size: 15px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.home-kpi-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(235, 61, 96, 0.22);
  color: #ffd5de;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.home-kpi.is-total .home-kpi-icon {
  background: #eb3d60;
  color: #fff;
}

.home-kpi.is-today,
.home-kpi.is-month,
.home-kpi.is-year,
.home-kpi.is-total {
  background: #1e1e1e;
}

.home-insight {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
  border-radius: 18px;
  padding: 16px 14px;
  min-height: 84px;
  color: #fff;
  background: #252525;
  box-shadow: 0 10px 22px rgba(30, 30, 30, 0.1);
}

.home-insight::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 12px 12px;
  pointer-events: none;
}

.home-insight > * {
  position: relative;
  z-index: 1;
}

.home-insight i {
  font-size: 22px;
  color: #eb3d60;
}

.home-insight span,
.home-insight small {
  display: block;
  opacity: 0.78;
  font-size: 12px;
}

.home-insight strong {
  display: block;
  font-size: 16px;
}

.home-insight.is-growth i {
  color: #86efac;
}

.home-insight.is-growth-down i {
  color: #eb3d60;
}

@media (max-width: 767px) {
  .home-kpi {
    min-height: 84px;
    padding: 12px;
  }

  .home-kpi-copy strong {
    font-size: 13px;
  }

  .home-kpi-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
}

.home-top-panel {
  background: #fff;
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 10px 24px rgba(30, 30, 30, 0.06);
  margin-bottom: 8px;
}

.home-top-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.home-top-head h5 {
  font-weight: 800;
}

.home-top-head p,
.home-top-head a {
  font-size: 13px;
}

.home-top-head p {
  color: #6b7280;
}

.home-top-head a {
  color: #eb3d60;
  font-weight: 700;
}

.home-top-item {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}

.home-top-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.home-top-rank {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(235, 61, 96, 0.12);
  color: #eb3d60;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12px;
}

.home-top-rank.is-first {
  background: #eb3d60;
  color: #fff;
}

.home-top-count {
  background: rgba(99, 102, 241, 0.1);
  color: #4f46e5;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}
</style>
